import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  OnInit,
  TemplateRef,
  ViewChild,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { skip, switchMap, tap } from 'rxjs';

import { TranslateModule } from '@ngx-translate/core';
import { MatrixGenericTableComponent } from '../../../../shared/components/generic-table/generic-table.component';
import type {
  GenericTableCellContext,
  GenericTableColumn,
  GenericTableStatusConfig,
} from '../../../../shared/components/generic-table/models/table-column.model';
import type { TableQuery } from '../../../../shared/components/generic-table/models/table-query.model';
import {
  MatrixCardComponent,
  MatrixCardHeaderDirective,
} from '../../../../shared/components/matrix-card/matrix-card.component';
import type { MatrixAnalyticsRow, MatrixAnalyticsTrend } from '../../models/matrix-analytics.model';
import { DASHBOARD_DATA } from '../../services/dashboard-data.token';
import { employeeStatusIconsGenericTableConfig } from '../../../../shared/constants/employee-status-icons.constants';

const MATRIX_ANALYTICS_STATUS_DOT_CLASS_BY_VALUE = {
  'Requires Review': 'bg-red-500',
  'Under Invest': 'bg-red-500',
  Referral: 'bg-violet-500',
  Suspended: 'bg-violet-500',
} as const satisfies Readonly<Record<string, string>>;

@Component({
  selector: 'app-matrix-analytics-card',
  standalone: true,
  imports: [
    CommonModule,
    MatrixGenericTableComponent,
    MatrixCardComponent,
    MatrixCardHeaderDirective,
    TranslateModule,
  ],
  templateUrl: './matrix-analytics-card.component.html',
})
export class MatrixAnalyticsCardComponent implements OnInit {
  readonly externalFilters = input<Record<string, string[]> | null>(null);

  private readonly dashboardData = inject(DASHBOARD_DATA);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('trendCell', { static: true })
  private readonly trendCellTpl!: TemplateRef<GenericTableCellContext<MatrixAnalyticsRow>>;

  @ViewChild('employeeCell', { static: true })
  private readonly employeeCellTpl!: TemplateRef<GenericTableCellContext<MatrixAnalyticsRow>>;

  @ViewChild('trendHeader', { static: true })
  private readonly trendHeaderTpl!: TemplateRef<{ col: GenericTableColumn<MatrixAnalyticsRow> }>;

  @ViewChild('statusHeaderTpl', { static: true })
  private readonly statusHeaderTpl!: TemplateRef<{ col: GenericTableColumn<MatrixAnalyticsRow> }>;

  readonly columns = signal<GenericTableColumn<MatrixAnalyticsRow>[]>([]);

  // table state
  readonly data = signal<MatrixAnalyticsRow[]>([]);
  readonly totalRecords = signal(0);
  readonly loading = signal(false);
  readonly rows = signal(10);
  readonly scrollHeight = '260px';

  private readonly querySig = signal<TableQuery>({ page: 0, rows: this.rows() });

  constructor() {
    toObservable(this.querySig)
      .pipe(
        tap((q) => {
          this.rows.set(q.rows);
          this.loading.set(true);
        }),
        switchMap((q) => this.dashboardData.getMatrixAnalytics(q)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (res) => {
          this.data.set([...res.data]);
          this.totalRecords.set(res.totalRecords);
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });

    toObservable(this.externalFilters)
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((filters) => {
        this.querySig.set({
          page: 0,
          rows: this.rows(),
          multiFilters: filters ?? {},
        });
      });
  }

  ngOnInit(): void {
    const analyticsStatusConfig: GenericTableStatusConfig<MatrixAnalyticsRow> = {
      ...employeeStatusIconsGenericTableConfig<MatrixAnalyticsRow>((row) => row.status),
      dotClassByValue: MATRIX_ANALYTICS_STATUS_DOT_CLASS_BY_VALUE,
    };

    this.columns.set([
      {
        field: 'employeeName',
        header: 'Employee',
        sortable: true,
        filterable: true,
        minWidth: '120px',
        width: '180px',
        cellTemplate: this.employeeCellTpl,
      },
      {
        field: 'jobRole',
        header: 'Job Role',
        sortable: true,
        filterable: true,
        minWidth: '90px',
        width: '120px',
      },
      {
        field: 'trend7dDirection',
        header: 'Real Time',
        sortable: false,
        filterable: false,
        align: 'center',
        minWidth: '90px',
        width: '120px',
        headerTemplate: this.trendHeaderTpl,
        cellTemplate: this.trendCellTpl,
      },
      {
        field: 'riskScore',
        header: 'Risk Score',
        sortable: true,
        filterable: true,
        align: 'center',
        minWidth: '90px',
        width: '120px',
        type: 'link',
        link: {
          routerLink: (row) => ['/analytics', row.guid],
          label: (row) => row.riskScore.toString(),
        },
      },
      {
        field: 'status',
        header: 'Analytics Status',
        sortable: true,
        filterable: true,
        noWrap: true,
        minWidth: '160px',
        width: '200px',
        type: 'status',
        status: analyticsStatusConfig,
        headerTemplate: this.statusHeaderTpl,
      },
    ]);
  }

  onQueryChange(query: TableQuery): void {
    this.querySig.set(query);
  }

  trendIcon(trend: MatrixAnalyticsTrend): string {
    if (trend === 'up') return '/icons/up-line.svg';
    if (trend === 'trending_up') return '/icons/up-right-line.svg';
    if (trend === 'trending_down') return '/icons/down-right-line.svg';
    if (trend === 'flat') return '/icons/down-right-line.svg';
    return '/icons/down-right-line.svg';
  }
}
